import React, { useEffect, useMemo } from 'react';

import { Text } from '@varicent/components';

import { useGetRootCustomHierarchiesInfo } from 'app/components/TerritoryMap/hooks/useGetRootCustomHierarchies';
import { useMapQuestion } from 'app/components/TerritoryMap/hooks/useMapQuestion';

import { SplitFeatures } from 'app/global/features';
import { PresetSegmentIds } from 'app/global/variables';

import {
  DefinitionFilterOperatorEnum,
  HierarchyTypeEnum,
  SortDirection
} from 'app/graphql/generated/graphqlApolloTypes';

import useTreatment from 'app/hooks/useTreatment';

import { CollectionFilter, Segment, SegmentFilterClause, SegmentField } from 'app/models';

import block from 'utils/bem-css-modules';
import { formatMessage } from 'utils/messages/utils';

import SegmentCard from './SegmentCard';
import style from './SegmentCards.module.pcss';

const b = block(style);

interface SegmentCardsProps {
  segments: Segment[];
  territoryRules: Array<unknown>;
  customHierarchyFilter: CollectionFilter<number>[];
  segmentField?: SegmentField;
  sortOrder?: SortDirection;
  searchText?: string;
}

const SegmentCards: React.FC<SegmentCardsProps> = ({
  segments,
  territoryRules,
  customHierarchyFilter,
  segmentField,
  sortOrder,
  searchText
}) => {
  const terCountForSegments = useMapQuestion('get-territory-counts-for-segments');
  const terCountForGeoOnlySegment = useMapQuestion('get-territory-counts-for-geo-only-segment');
  const [isGenerateFilterFromTerritoryOn] = useTreatment(SplitFeatures.GENERATE_FILTER_FROM_TERRITORY);
  const [isSingleCustomHierarchyMapOn] = useTreatment(SplitFeatures.MAP_SINGLE_CUSTOM_HIERARCHY);
  const { rootCustomHierarchies } = useGetRootCustomHierarchiesInfo(!isSingleCustomHierarchyMapOn);

  const filteredSegments = useMemo(() => {
    if (!searchText) return segments;

    const text = searchText.toLowerCase();
    return segments.filter((segment) => segment.segmentName.toLowerCase().includes(text));
  }, [segments, searchText]);

  useEffect(() => {
    terCountForSegments.ask();
    terCountForGeoOnlySegment.ask();
  }, [segments, territoryRules]);

  const presetMatchAnyFilterClauses: SegmentFilterClause[] = useMemo(
    () =>
      rootCustomHierarchies.map((customHierarchy) => ({
        hierarchies: [],
        operator: DefinitionFilterOperatorEnum.MATCH_ANY_CONDITION,
        rootHierarchyId: customHierarchy.rootHierarchyId,
        rootHierarchyName: customHierarchy.rootName,
        hierarchyType: HierarchyTypeEnum.CustomHierarchy
      })),
    [rootCustomHierarchies]
  );

  const sortedSegments = useMemo(() => {
    if (!segmentField || !sortOrder || segments.length === 0) {
      return filteredSegments;
    }

    const sorted = [...filteredSegments].sort((a, b) => {
      let comparison = 0;

      switch (segmentField) {
        case 'segmentName': {
          comparison = a.segmentName.localeCompare(b.segmentName);
          break;
        }

        case 'territoryCount': {
          const countA = terCountForSegments.answer?.get(a.segmentId) ?? 0;
          const countB = terCountForSegments.answer?.get(b.segmentId) ?? 0;
          comparison = countA - countB;
          break;
        }

        default:
          return 0;
      }

      return sortOrder === SortDirection.desc ? -comparison : comparison;
    });

    return sorted;
  }, [filteredSegments, segmentField, sortOrder, terCountForSegments.answer]);

  return (
    <div className={b()}>
      <Text className={b('title')}>{formatMessage('SELECT_A_SEGMENT')}</Text>
      {isGenerateFilterFromTerritoryOn && (
        <>
          <SegmentCard
            customHierarchyFilter={customHierarchyFilter}
            territoriesCount={terCountForGeoOnlySegment.answer}
            isLoadingTerritoriesCount={terCountForGeoOnlySegment.isLoading}
            name={formatMessage('GEO_BASED_RULES_ONLY')}
            key="geo-based-rules"
            id={PresetSegmentIds.simpleOnly}
            filter={[]}
            orphanCount={0}
            rootOrphanCount={0}
            isPresetSegment={true}
          />
          <SegmentCard
            customHierarchyFilter={customHierarchyFilter}
            territoriesCount={territoryRules.length}
            isLoadingTerritoriesCount={false}
            name={formatMessage('ALL_HIERARCHIES')}
            key="all-hierarchies"
            id={PresetSegmentIds.allRules}
            filter={presetMatchAnyFilterClauses}
            orphanCount={0}
            rootOrphanCount={0}
            isPresetSegment={true}
          />
        </>
      )}

      {sortedSegments.map((segment) => (
        <SegmentCard
          customHierarchyFilter={customHierarchyFilter}
          territoriesCount={terCountForSegments.answer?.get(segment.segmentId) ?? null}
          isLoadingTerritoriesCount={terCountForSegments.isLoading}
          name={segment.segmentName}
          key={segment.segmentId}
          id={segment.segmentId}
          filter={segment.clauses}
          orphanCount={segment.orphanCount}
          rootOrphanCount={segment.rootOrphanCount}
        />
      ))}
    </div>
  );
};

export default SegmentCards;
